import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, ArrowLeft, Loader2, Zap, Music, BellOff } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePublicAuth } from "../context/PublicAuthContext";
import api from "../utils/api";

function ArtistFollowCard({ artist, onUnfollow }) {
  return (
    <Link
      to={`/artist/${artist.slug}`}
      className="group relative flex flex-col rounded-2xl overflow-hidden bg-zinc-900/50 border border-white/[0.05] hover:border-white/10 transition-all duration-200 hover:shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
    >
      {/* Image */}
      <div className="aspect-square overflow-hidden bg-zinc-800">
        {artist.bild_url
          ? <img src={artist.bild_url} alt={artist.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          : <div className="w-full h-full flex items-center justify-center"><Music size={24} className="text-zinc-700" /></div>
        }
      </div>
      {/* Info */}
      <div className="p-3 flex-1 flex flex-col">
        <p className="text-white text-sm font-bold truncate flex-1">{artist.name}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-zinc-700 text-xs">{artist.genre || "Artist"}</span>
          <button
            onClick={e => { e.preventDefault(); e.stopPropagation(); onUnfollow(artist.id); }}
            className="flex items-center gap-1 text-zinc-700 hover:text-rose-400 transition-colors text-xs p-1 hover:bg-rose-500/5 rounded-lg"
            title="Entfolgen"
          >
            <BellOff size={11} />
          </button>
        </div>
      </div>
    </Link>
  );
}

export default function UserFollowsPage() {
  const { publicUser, isPublicAuth, publicApi, isPublicLoading } = usePublicAuth();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPublicLoading || !isPublicAuth) { setLoading(false); return; }
    const load = async () => {
      try {
        const followRes = await publicApi("get", "/me/follows");
        const ids = followRes.data.map(f => f.artist_id).filter(Boolean);
        if (!ids.length) { setLoading(false); return; }
        const artists = await Promise.all(
          ids.map(id => api.get(`/artists/${id}`).then(r => r.data).catch(() => null))
        );
        setArtists(artists.filter(Boolean));
      } catch { /* silent */ } finally { setLoading(false); }
    };
    load();
  }, [isPublicAuth, isPublicLoading]); // eslint-disable-line

  const handleUnfollow = async (artistId) => {
    try {
      await publicApi("delete", `/me/follows/${artistId}`);
      setArtists(prev => prev.filter(a => a.id !== artistId));
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        {/* Back + Title */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/profil" className="text-zinc-500 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-xl">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2">
              <Bell size={20} className="text-zinc-400" /> Following
            </h1>
            {!loading && <p className="text-zinc-600 text-sm mt-0.5">{artists.length} gefolgte Artists</p>}
          </div>
        </div>

        {/* Not logged in */}
        {!isPublicLoading && !isPublicAuth && (
          <div className="text-center py-20">
            <p className="text-zinc-500 mb-4">Du musst angemeldet sein.</p>
          </div>
        )}

        {/* Not supporter */}
        {!isPublicLoading && isPublicAuth && !publicUser?.is_supporter && (
          <div className="rounded-2xl bg-zinc-900/50 border border-white/5 p-8 text-center">
            <Bell size={32} className="text-zinc-700 mx-auto mb-3" />
            <h2 className="text-white font-bold text-lg mb-2">Artist-Follows sind ein Supporter-Feature</h2>
            <p className="text-zinc-500 text-sm mb-5">Folge deinen Lieblings-Artists und verpasse keine neuen Releases – ab 3€/Monat.</p>
            <Link to="/supporter" className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm transition-colors">
              <Zap size={14} /> Jetzt Supporter werden
            </Link>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-zinc-600" /></div>
        )}

        {/* Empty */}
        {!loading && isPublicAuth && publicUser?.is_supporter && artists.length === 0 && (
          <div className="text-center py-20">
            <Bell size={40} className="text-zinc-800 mx-auto mb-4" />
            <p className="text-zinc-500">Du folgst noch keinen Artists.</p>
            <Link to="/artists" className="mt-4 inline-block text-zinc-400 hover:text-white text-sm transition-colors">
              Artists entdecken →
            </Link>
          </div>
        )}

        {/* Grid */}
        {!loading && artists.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {artists.map(a => (
              <ArtistFollowCard key={a.id} artist={a} onUnfollow={handleUnfollow} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
